import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const skipFetchRef = useRef(false);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      if (skipFetchRef.current) {
        skipFetchRef.current = false;
        setLoading(false);
      } else {
        fetchUser();
      }
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const [userRes, permRes] = await Promise.all([
        axios.get(`${API_URL}/auth/me`),
        axios.get(`${API_URL}/auth/permissions`)
      ]);
      setUser(userRes.data);
      setPermissions(permRes.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const data = response.data;
    
    // Check if MFA is required
    if (data.mfa_required) {
      return data; // Return MFA challenge to caller
    }
    
    const { access_token, user: userData } = data;
    
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    
    // Skip the useEffect re-fetch since we already have user data
    skipFetchRef.current = true;
    
    // Fetch permissions before setting state
    let permsData = null;
    try {
      const permRes = await axios.get(`${API_URL}/auth/permissions`);
      permsData = permRes.data;
    } catch (e) {
      console.error('Failed to fetch permissions:', e);
    }
    
    // Set all state together
    setUser(userData);
    setPermissions(permsData);
    setToken(access_token);
    
    return userData;
  };

  const register = async (email, password, name, role, refCode = null) => {
    const payload = { email, password, name, role };
    if (refCode) payload.ref_code = refCode;
    const response = await axios.post(`${API_URL}/auth/register`, payload);
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    
    // Skip the useEffect re-fetch since we already have user data
    skipFetchRef.current = true;
    
    // Fetch permissions before setting state
    let permsData = null;
    try {
      const permRes = await axios.get(`${API_URL}/auth/permissions`);
      permsData = permRes.data;
    } catch (e) {
      console.error('Failed to fetch permissions:', e);
    }
    
    // Set all state together
    setUser(userData);
    setPermissions(permsData);
    setToken(access_token);
    
    return userData;
  };

  const demoLogin = async () => {
    // Strip any stale Authorization header from a prior session — defensive
    // so cross-origin preflights & backend middleware never see a bad Bearer
    // token on the public demo-login call.
    delete axios.defaults.headers.common['Authorization'];

    const postWithRetry = async () => {
      try {
        return await axios.post(`${API_URL}/auth/demo-login`, {}, { timeout: 15000 });
      } catch (err) {
        // Single retry only on network / 5xx errors — never on 4xx (client error).
        const isNetwork = !err.response;
        const is5xx = err.response && err.response.status >= 500;
        if (isNetwork || is5xx) {
          await new Promise((r) => setTimeout(r, 1200));
          return axios.post(`${API_URL}/auth/demo-login`, {}, { timeout: 15000 });
        }
        throw err;
      }
    };

    const response = await postWithRetry();
    const { access_token, user: userData } = response.data;

    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

    skipFetchRef.current = true;

    let permsData = null;
    try {
      const permRes = await axios.get(`${API_URL}/auth/permissions`);
      permsData = permRes.data;
    } catch (e) {
      console.error('Failed to fetch permissions:', e);
    }

    setUser(userData);
    setPermissions(permsData);
    setToken(access_token);

    return userData;
  };

  const logout = async () => {
    /*
     * Comprehensive sign-out with security hardening:
     * 1. Tell backend to invalidate token (best effort — audit logged)
     * 2. Wipe ALL auth + credential + user-profile data from localStorage
     * 3. Clear sessionStorage entirely
     * 4. Drop axios default Authorization header
     * 5. Reset in-memory auth state (user, permissions, token)
     */

    // 1. Backend notification — graceful on failure so logout always completes
    try {
      if (token) {
        await axios.post(`${API_URL}/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 5000,
        });
      }
    } catch (e) {
      // Non-blocking — sign out must always succeed client-side
      console.debug('Backend logout signal failed (non-fatal):', e?.message);
    }

    // 2. Wipe every key that might expose the previous user's data
    const SENSITIVE_KEYS = [
      'token', 'user',
      'aws_creds', 'azure_creds', 'gcp_creds', 'snowflake_creds',
      'github_token', 'github_user', 'gitlab_token', 'gitlab_user',
      'onboarding_completed', 'onboardingCompleted', 'hasSeenOnboarding',
      'selected_providers', 'cartie_privacy_settings', 'cartie_session_id',
      'cartie_quickstart_complete', 'cartie_quickstart_data', 'cartie_quickstart_skipped',
      'cartie_tour_completed', 'changelog_last_seen',
    ];
    SENSITIVE_KEYS.forEach((k) => {
      try { localStorage.removeItem(k); } catch (_) { /* ignore */ }
    });

    // 3. sessionStorage — nuke everything for defence-in-depth
    try { sessionStorage.clear(); } catch (_) { /* ignore */ }

    // 4. Drop axios auth header so ghost requests can't leak the token
    delete axios.defaults.headers.common['Authorization'];

    // 5. Reset in-memory state
    setToken(null);
    setUser(null);
    setPermissions(null);
  };

  const hasRole = (roles) => {
    if (!user) return false;
    return roles.includes(user.role);
  };

  // Check if user has specific permission
  const hasPermission = (module, action) => {
    if (!permissions?.permissions) return false;
    const modulePerms = permissions.permissions[module];
    if (!modulePerms) return false;
    return modulePerms[action] === true;
  };

  // Check if user can access a specific feature
  const canAccess = (feature) => {
    if (!permissions?.permissions) return false;
    
    const featureMap = {
      'dashboard': ['dashboard', 'view'],
      'anomalies': ['anomalies', 'view'],
      'recommendations': ['recommendations', 'view'],
      'guardrails': ['guardrails', 'view'],
      'reports': ['reports', 'view'],
      'settings': ['settings', 'view'],
      'users': ['users', 'view'],
      'teams': ['teams', 'view'],
      'cloud_pl': ['executive', 'cloud_pl'],
      'tech_spend': ['executive', 'tech_spend'],
      'customer_economics': ['executive', 'customer_economics'],
      'board_deck': ['executive', 'board_deck'],
      'git_to_bill': ['revolutionary', 'git_to_bill'],
      'time_machine': ['revolutionary', 'time_machine'],
      'autopilot': ['revolutionary', 'autopilot'],
      'autopilot_execute': ['revolutionary', 'autopilot_execute'],
      'ai_advisor': ['ai_advisor', 'view'],
    };

    const mapping = featureMap[feature];
    if (!mapping) return true; // Default to allow if not mapped
    
    return hasPermission(mapping[0], mapping[1]);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      demoLogin,
      register, 
      logout,
      hasRole,
      hasPermission,
      canAccess,
      permissions,
      isAdmin: user?.role === 'admin',
      isFinance: user?.role === 'finance',
      isEngineering: user?.role === 'engineering'
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
